import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import GlobalContext from '../contexts/global.js';
import UserContext, { isPartner, isDirector } from '../contexts/user.js';
import copyToClipboard from '../helpers/copyToClipboard.js';
import StandardDate from './StandardDate.js';
import PageReactions from './PageReactions.js';
import Pill from './Pill.js';

import '../styles/pageCard.css';

const PageCard = ({
    page,
    badgeKeys = [],
    showFooter = true,
    footerActions = null,
    children
}) => {
    const { globalContext } = useContext(GlobalContext);
    const { userContext } = useContext(UserContext);
    const [copied, setCopied] = useState(false);

    const categories = (page.data?.category || []).map(category => {
        const categoryData = globalContext?.categories.find(availableCategory => availableCategory.key === category) || {};
        return { key: category, label: categoryData.value || category };
    });

    /* One badge per tagged value */
    const badges = badgeKeys
        .flatMap(badgeKey => {
            const { key, icon } = typeof badgeKey === "string" ? { key: badgeKey, icon: null } : badgeKey;
            const values = [ page.data?.[key] ].flat().filter(Boolean);
            const property = globalContext?.extraPageFields?.properties?.find(candidate => candidate.id === key);
            return values.map(value => ({
                key,
                value,
                icon: typeof icon === "function" ? icon(value) : icon,
                label: property?.items?.find(item => item.key === value)?.value ?? value
            }));
        })

    /* Partners/Director badges only show to respective groups - a page tagged with both shows both, to a viewer who is both */
    const rawGradeName = page.data?.gradeName;
    const gradeNameValues = (Array.isArray(rawGradeName) ? rawGradeName : [rawGradeName]).filter(Boolean);
    const gradeNameProperty = globalContext?.extraPageFields?.properties?.find(candidate => candidate.id === "gradeName");

    false && gradeNameValues
        .filter(value => (value === "partners" && isPartner(userContext)) || (value === "director" && isDirector(userContext)))
        .forEach(value => {
            badges.push({
                key: "gradeName",
                value,
                icon: "people",
                label: gradeNameProperty?.items?.find(item => item.key === value)?.value ?? "Partners",
                variant: "partners"
            });
        });

    const copyLink = () => {
        copyToClipboard(`${window.location.origin}/story/${page.urn}`);
        setCopied(true);

        window.setTimeout(() => {
            setCopied?.(false);
        }, 5000);
    };

    return (
        <div className="page-card">
            <div className="card-header">
                <div className="tags">
                    {
                        categories.map(category => (
                            <Link
                                key={category.key}
                                className="category"
                                to={`/property/category/${category.key}`}
                            >
                                {category.label}
                            </Link>
                        ))
                    }

                    { page.data?.author && (
                        <span className="author">
                            By <strong>{ page.data.author }</strong>
                        </span>
                    )}

                    <StandardDate
                        date={new Date(page.publishDate)}
                        inline={true}
                        className="date"
                    />
                </div>

                {
                    badges.length > 0 && (
                        <div className="badges">
                            { badges.map(badge => (
                                <Pill
                                    key={`${badge.key}-${badge.value}`}
                                    label={badge.label}
                                    icon={badge.icon}
                                    variant={badge.variant}
                                    to={`/property/${badge.key}/${badge.value}`}
                                />
                            )) }
                        </div>
                    )
                }
            </div>

            { children }

            {
                showFooter && (
                    <div className="card-footer">
                        <div className="footer-actions">
                            { footerActions }
                        </div>

                        <div className="spacer"></div>

                        <button
                            type="button"
                            className="action copy-link"
                            onClick={copyLink}
                        >
                            <img
                                className="action-icon"
                                src={`${process.env.PUBLIC_URL}/assets/icons/link.svg`}
                            />

                            { copied ? "Link copied" : "Copy story link" }
                        </button>

                        <PageReactions
                            pageIndex={page.index}
                            prompt=""
                            showDislike={false}
                        />
                    </div>
                )
            }
        </div>
    );
};

export default PageCard;
